/**
 * What languages exist, and what a namespace is called.
 *
 * Deliberately free of any i18next import: `index.ts` initialises the library
 * and needs these constants, and so does the language selector, which must not
 * drag the whole engine into a component that only draws a menu.
 */

/** A language this build ships. Adding one means adding a folder under `locales/`. */
export interface Language {
  code: LanguageCode;
  /** The name of the language **in that language** — never translated. */
  label: string;
  /** For the selector. A region flag, not a language flag; see ./README.md. */
  flag: string;
}

export const LANGUAGE_CODES = ['en', 'cs'] as const;
export type LanguageCode = (typeof LANGUAGE_CODES)[number];

export const LANGUAGES: readonly Language[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
];

/**
 * The language every missing key falls back to.
 *
 * English is also the one the key types are generated from, so it is the only
 * catalogue guaranteed to be complete.
 */
export const FALLBACK_LANGUAGE: LanguageCode = 'en';

/** Where the choice is remembered. Mirrors `forsys.colorMode`. */
export const LANGUAGE_STORAGE_KEY = 'forsys.language';

/**
 * The namespaces, one per area of the product.
 *
 * `common` is loaded by everything and holds only what genuinely appears on
 * more than one screen — a namespace that becomes a dumping ground stops being
 * a useful split.
 */
export const NAMESPACES = [
  'common',
  'nav',
  'errors',
  'auth',
  'system',
  'files',
  'inbox',
  'people',
  'notifications',
  'schedules',
  'audit',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/** Whether `value` is a language this build ships. */
export function isLanguageCode(value: unknown): value is LanguageCode {
  return typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value);
}
